import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  CheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  StarIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";
import { ForwardRefExoticComponent, SVGProps } from "react";
import { useState, useEffect } from "react";
import asianStudentImage from "../../assets/asian-martial-arts-student.jpg";
import femaleStudentImage from "../../assets/female-martial-arts-student.jpg";
import testimonialImage from "../../assets/jason_greenfield.png";
import dashboardScreenshot from "../../assets/dashboard-screenshot.jpg";
import dojoTrackVideo from "../../assets/DojoTrack_Quick_Overview.mp4";
import maxresdefault from "../../assets/maxresdefault.jpg";

interface Feature {
  name: string;
  description: string;
  icon: ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
}

const testimonials = [
  {
    content:
      "DojoTrack has made running our martial arts studio so much easier. Tracking students and keeping up with belt progress is super simple now, and it saves us a ton of time every week. The AI features are pretty insightful, too!",
    author: "Jason Greenfield",
    role: "Owner, Theta Combat",
    image: testimonialImage,
    rating: 5,
  },
];

const features: Feature[] = [
  {
    name: "AI-Powered Student Management",
    description:
      "Complete student lifecycle management with AI insights to reduce drop-offs and increase retention. Track progress, manage memberships, and predict student success.",
    icon: UserGroupIcon,
  },
  {
    name: "Smart Analytics Dashboard",
    description:
      "Real-time insights into your studio's performance with actionable metrics. AI identifies growth opportunities and enables data-driven decisions.",
    icon: ChartBarIcon,
  },
  {
    name: "Automated Lead Conversion",
    description:
      "Convert more prospects with AI-powered lead scoring, automated follow-ups, and seamless appointment scheduling. Turn leads into loyal students.",
    icon: CalendarIcon,
  },
];

const benefits = [
  "Save 10+ hours per week on admin tasks",
  "Increase student retention by 25%",
  "Automate payment processing with Stripe",
  "AI predicts which students might leave",
  "Digital waivers and forms",
  "Real-time attendance tracking",
  "Professional email & SMS messaging",
  "Instant lead follow-up with automated SMS appointment booking",
  "Smart payment reminders to prevent revenue loss from expired cards",
  "Seamless event registration with integrated payment processing",
  "Self-service payment portal for students and parents",
  "Intelligent class check-in to prevent attendance errors",
  "Touchless student sign-in kiosk for contactless attendance",
  "Automated belt promotion system after testing completion",
  "Built-in point of sale for merchandise and one-time charges",
  "Easy data migration from your existing CRM system",
];

const MetaLanding = () => {
  const location = useLocation();
  const [showVideo, setShowVideo] = useState(false);

  // Check if user was redirected back after form submission
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('submitted') === 'true') {
      setShowVideo(true);
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Best Martial Arts Studio Software & CRM for Dojos | DojoTrack</title>
        <meta
          name="description"
          content="Join martial arts studios using DojoTrack's AI software. Student management, payments, analytics & more. Start your 14-day free trial today!"
        />
        <meta
          name="keywords"
          content="dojotrack, software martial arts, AI software martial arts, martial arts crm, martial arts management software, dojo software, student management, attendance tracking, martial arts studio software"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta
          property="og:title"
          content="Best Martial Arts Studio Software & CRM for Dojos | DojoTrack"
        />
        <meta
          property="og:description"
          content="Join martial arts studios using DojoTrack's AI software. Student management, payments, analytics & more. Start your 14-day free trial today!"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/martial-arts-studio-software" />
        <meta property="og:image" content="https://dojotrack.com/social-media-share-dojotrack.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Martial Arts Studio Software & CRM for Dojos | DojoTrack"
        />
        <meta
          name="twitter:description"
          content="Join martial arts studios using DojoTrack's AI software. Student management, payments, analytics & more. Start your 14-day free trial today!"
        />
        <meta name="twitter:image" content="https://dojotrack.com/social-media-share-dojotrack.png" />
        <link rel="canonical" href="https://dojotrack.com/martial-arts-studio-software" />
      </Helmet>
      
      <div className="bg-white dark:bg-gray-900">
        {/* Hero section - Centered content with conditional video */}
        <div className="relative isolate overflow-hidden bg-gradient-to-b from-indigo-100/20 dark:from-indigo-900/20">
          {/* Textured background overlay */}
          <div className="absolute inset-0 opacity-20 dark:opacity-10 z-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
          {/* Geometric pattern overlay */}
          <div className="absolute inset-0 opacity-30 dark:opacity-20 z-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23ffffff' stroke-width='0.5' opacity='0.1'/%3E%3C/pattern%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E\")"}}></div>
          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 z-0"></div>
          
          {/* Ensure text selection works */}
          <style>{`
            .select-text {
              user-select: text !important;
              -webkit-user-select: text !important;
              -moz-user-select: text !important;
              -ms-user-select: text !important;
            }
            .select-text * {
              user-select: text !important;
              -webkit-user-select: text !important;
              -moz-user-select: text !important;
              -ms-user-select: text !important;
            }
          `}</style>
          
          {!showVideo ? (
            /* Layout with form and video side by side */
            <div className="mx-auto max-w-7xl px-6 pb-16 pt-8 sm:pb-20 lg:px-8 lg:py-20">
              {/* Form and Video Section */}
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12 items-center">
                {/* Left: Headline and Form */}
                <div className="lg:col-span-2 relative z-20">
                  <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl mb-4 sm:mb-6">
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      The #1 AI Software
                    </span>
                    <br />
                    <span className="text-gray-900 dark:text-white">
                      for Martial Arts Studios
                    </span>
                  </h1>
                  <p className="text-base sm:text-lg leading-7 sm:leading-8 text-gray-600 dark:text-gray-400 mb-6 sm:mb-8">
                    <strong>Enter your information</strong> below to watch our <strong>exclusive demo</strong> and see exactly how DojoTrack{" "}
                    <strong>transforms</strong> martial arts studio management with <strong>AI-powered</strong> automation.
                  </p>
                  <div className="flex flex-col gap-3">
                    <form 
                      action="https://formspree.io/f/xkgqrlje"
                      method="POST"
                      className="flex flex-col gap-3 w-full"
                    >
                      <input type="hidden" name="_next" value="https://dojotrack.com/martial-arts-studio-software?submitted=true" />
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="email"
                          name="email"
                          id="email"
                          required
                          placeholder="Email Address"
                          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <button
                          type="submit"
                          className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-500 hover:to-purple-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
                        >
                          See How It Works
                        </button>
                      </div>
                    </form>
                  </div>
                  <p className="mt-4 text-xs sm:text-xs text-gray-400 dark:text-gray-500 text-left">
                    By providing your email, you agree to receive periodic emails from DojoTrack LLC. Your information will never be shared. View our <Link to="/terms" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">Terms of Service</Link> & <Link to="/privacy-policy" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">Privacy Policy</Link>.
                  </p>
                </div>

                {/* Right: Video-like Image with Overlay */}
                <div className="lg:col-span-3 flex items-center">
                  <div className="w-full relative">
                    <div className="shadow-lg md:rounded-3xl overflow-hidden">
                      <div className="bg-white dark:bg-gray-900">
                        <div className="relative">
                          <div className="aspect-video w-full relative">
                            <img
                              src={maxresdefault}
                              alt="DojoTrack Demo Video"
                              className="w-full h-full rounded-lg opacity-30 pointer-events-none object-cover"
                            />
                            {/* Overlay */}
                            <div className="absolute inset-0 bg-gray-900/60 flex items-center justify-center rounded-lg">
                              <div className="text-center p-4">
                                <div className="mb-3">
                                  <svg className="w-12 h-12 mx-auto text-white/80" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">
                                  Enter your information to watch the full demo
                                </h3>
                                <p className="text-white/80 text-xs">
                                  See exactly how DojoTrack transforms your studio
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Video - shown after email submission */
            <div className="mx-auto max-w-7xl px-6 pb-16 pt-8 sm:pb-20 lg:px-8 lg:py-20">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12 items-center min-h-[400px]">
                {/* Left: Text Content (kept from before) */}
                <div className="lg:col-span-2 relative z-20 select-text">
                  <h1 className="mt-10 text-4xl font-bold tracking-tight sm:text-6xl text-left">
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      The #1 AI Software
                    </span>
                    <br />
                    <span className="text-gray-900 dark:text-white">
                      for Martial Arts Studios
                    </span>
                  </h1>
                  <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400 text-left">
                    <strong>Enter your email</strong> below to watch our <strong>exclusive demo</strong> and see exactly how DojoTrack{" "}
                    <strong>transforms</strong> martial arts studio management with <strong>AI-powered</strong> automation.
                  </p>
                  <p className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-left">
                    By opting in, you agree to receive periodic text messages & emails from Spark Membership Software, LLC. Your information will never be shared. Reply STOP to cancel. Standard rates may apply. View our <Link to="/terms" className="underline hover:text-gray-600 dark:hover:text-gray-300">Terms of Service</Link> & <Link to="/privacy-policy" className="underline hover:text-gray-600 dark:hover:text-gray-300">Privacy Policy</Link>.
                  </p>
                </div>

                {/* Right: YouTube Video */}
                <div className="lg:col-span-3 flex items-center">
                  <div className="w-full">
                    <div className="shadow-lg md:rounded-3xl overflow-hidden">
                      <div className="bg-white dark:bg-gray-900">
                        <div className="relative">
                          <div className="aspect-video w-full relative">
                            <iframe
                              className="w-full h-full rounded-lg"
                              src="https://www.youtube.com/embed/cvyQQDnG4UM?autoplay=1&mute=1&loop=1&playlist=cvyQQDnG4UM"
                              title="DojoTrack Demo Video"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 text-center">
                      <Link
                        to="/signup"
                        className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-500 hover:to-purple-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        Start Your 14-Day Free Trial Today
                      </Link>
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                        Free 14-day trial • Card required • Cancel anytime
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white dark:from-gray-900 sm:h-32" />
        </div>

        {/* Pricing section */}
        <div className="mx-auto max-w-7xl px-6 pt-24 pb-0 sm:py-32 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 p-8 shadow-xl">
                <div className="text-left">
                  <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
                    Simple, Transparent Pricing
                  </h2>
                  <p className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                    <span className="text-gray-900 dark:text-white">Everything you need, starting at just </span>
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">$95/month</span>
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
                    No hidden fees, no feature restrictions, no surprises. Get full access to all DojoTrack features.
                  </p>
                  <div className="mt-8">
                    <Link
                      to="/signup"
                      className="inline-flex items-center rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-3 text-lg font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all transform hover:scale-105 hover:shadow-xl"
                    >
                      Start Your 14-Day Free Trial
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Dashboard Screenshot */}
            <div className="lg:col-span-3 flex items-center">
              <div className="w-full">
                <img
                  src={dashboardScreenshot}
                  alt="DojoTrack Dashboard Screenshot"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Benefits section */}
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Why Martial Arts Studios Choose DojoTrack
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Everything you need to run a successful martial arts studio
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
              Stop juggling multiple tools and spreadsheets. DojoTrack's AI-powered platform 
              handles everything from student management to payment processing.
            </p>
          </div>
          
          <div className="mx-auto mt-16 max-w-4xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`flex items-start bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                    index === benefits.length - 1 ? 'lg:col-start-2' : ''
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <p className="ml-3 text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials section */}
        <div className="bg-gray-50 dark:bg-gray-800/50">
          <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Testimonials
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Loved by martial arts studio owners
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex flex-col justify-between bg-white dark:bg-gray-800 p-8 ring-1 ring-gray-200 dark:ring-gray-700 rounded-2xl shadow-sm"
              >
                <div>
                  <div className="flex items-center justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                    ))}
                  </div>
                  <blockquote className="text-gray-900 dark:text-white text-center">
                    <p className="text-lg leading-7">"{testimonial.content}"</p>
                  </blockquote>
                </div>
                <div className="mt-6 flex items-center justify-center gap-x-4">
                  <img
                    className="h-10 w-10 rounded-full bg-gray-50 dark:bg-gray-700"
                    src={testimonial.image}
                    alt=""
                  />
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {testimonial.author}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* Features section */}
        <div className="bg-white dark:bg-gray-900">
          <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Powerful Features
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">AI-powered tools</span>
              <span className="text-gray-900 dark:text-white"> that grow your studio</span>
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.name} className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900 dark:text-white mb-4">
                    <feature.icon
                      className="h-5 w-5 flex-none text-indigo-600 dark:text-indigo-400"
                      aria-hidden="true"
                    />
                    {feature.name}
                  </dt>
                  <dd className="flex flex-col text-base leading-7 text-gray-600 dark:text-gray-400 flex-1">
                    <p className="flex-1">{feature.description}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          </div>
        </div>

        {/* Data Migration section */}
        <div className="bg-gray-50 dark:bg-gray-800/50">
          <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Migration From Your Other Platform</span>
              <br />
              Made Easy
            </h2>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
              Don't let the fear of switching platforms hold you back. Seamlessly bring your existing students, leads, and payment data into DojoTrack in just a few simple steps.
            </p>
          </div>

          {/* Quick Steps */}
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 1</div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Export Your Data</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Export students or leads from your current CRM as <strong>CSV</strong>, <strong>Excel</strong>, or <strong>JSON</strong>.</p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 2</div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Upload to DojoTrack</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Use our guided importer with intelligent column matching to map your fields quickly.</p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 3</div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Connect Stripe (Optional)</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Already on Stripe? Import customers and keep payment methods synced.</p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 4</div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Notify Everyone Once</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Send a one‑time Email or SMS using the <span className="font-mono">{'{{payment_link}}'}</span> tag so families can update their payment method in minutes.</p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 5</div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">Create Memberships & Start Billing</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Set up membership plans with pricing, assign them to students, and start collecting monthly tuition automatically through Stripe.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 flex flex-col items-center">
            <Link
              to="/data-migration"
              className="rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-lg font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mb-3 transition-all transform hover:scale-105 hover:shadow-xl"
            >
              Learn More About Migration
            </Link>
            <Link
              to="/signup"
              className="text-indigo-600 dark:text-indigo-400 hover:underline text-base"
            >
              Start Your Free Trial
            </Link>
          </div>
          </div>
        </div>

        {/* Final CTA section */}
        <div className="bg-indigo-600 dark:bg-indigo-700">
          <div className="mx-auto max-w-7xl px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
            <div className="mx-auto max-w-2xl text-left sm:text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to transform your studio?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-indigo-100">
                Join other martial arts studios already using DojoTrack. 
                Start your 14-day free trial today.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-center gap-x-6 gap-y-4">
                <Link
                  to="/signup"
                  className="rounded-md bg-white px-6 py-3 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Start 14-Day Free Trial
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-semibold leading-6 text-white"
                >
                  View pricing <span aria-hidden="true">→</span>
                </Link>
              </div>
              <p className="mt-4 text-sm text-indigo-100">
                14-day free trial, No setup fees, Cancel anytime, 5-minute setup
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetaLanding;
